import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Users as UsersIcon, Globe, Instagram, Linkedin, MapPin, X, Eye, EyeOff,
} from "lucide-react";

// Iter 39 — "Edit your profile" popup for /members. Same shape as the
// first-sign-in onboarding modal (portal, backdrop, scroll-lock, sticky
// footer), but this one is user-dismissable and includes an
// Unpublish/Publish toggle so the founder can manage visibility without
// leaving the Members Directory.
const DIR_CATEGORIES = ["food", "beverage", "supplement", "wellness", "beauty", "pet", "other"];
const DIR_STAGES = [
  ["idea", "Idea"], ["pre-launch", "Pre-launch"], ["launched", "Launched"],
  ["in-retail", "In retail"], ["scaling", "Scaling"],
];

export default function EditCommunityProfileModal({ open, onClose, onSaved }) {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Seed the form from the latest server state each time the modal
  // opens — cheap network hop but guarantees we never render a stale
  // draft after the user has edited elsewhere.
  useEffect(() => {
    if (!open) { setProfile(null); return; }
    setLoading(true);
    api.get("/auth/me")
      .then((r) => {
        const dir = r.data?.directory || {};
        setProfile({
          brand: dir.brand || "", bio: dir.bio || "", city: dir.city || "",
          category: dir.category || "", stage: dir.stage || "",
          website: dir.website || "", instagram: dir.instagram || "",
          linkedin: dir.linkedin || "",
          published: !!r.data?.directory_published,
        });
      })
      .catch(() => setProfile({
        brand: "", bio: "", city: "", category: "", stage: "",
        website: "", instagram: "", linkedin: "", published: false,
      }))
      .finally(() => setLoading(false));
  }, [open, user?.id]);

  // Body scroll-lock while open + ESC to close.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const canPublish = !!(profile?.brand?.trim() && profile?.category);

  const save = async (publish) => {
    if (!profile) return;
    setSaving(true);
    try {
      const body = { ...profile, published: publish !== undefined ? publish : profile.published };
      const { data } = await api.put("/auth/profile/directory", body);
      setProfile((p) => ({ ...p, published: !!data.published }));
      // Refresh auth user so any header/menu chips that read
      // `directory_published` update immediately too.
      refresh?.();
      toast.success(data.published ? "Live in the directory" : "Saved — not yet published");
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10400] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-community-profile-heading"
      data-testid="edit-community-profile-modal"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-shell-side/80 backdrop-blur-lg"
        onClick={() => !saving && onClose?.()}
      />

      <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-shell-border overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-b border-shell-border p-5 sm:p-6 md:p-7 pr-14">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center flex-shrink-0">
              <UsersIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-primary font-bold">
                Community profile
              </p>
              <h2
                id="edit-community-profile-heading"
                className="mt-1 font-display text-xl sm:text-2xl md:text-3xl leading-tight"
              >
                Edit your profile
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-muted-foreground">
                Show up in the Members Directory. Publish makes you discoverable — unpublish hides you without deleting your details.
              </p>
              {profile && (
                <span
                  data-testid="edit-community-profile-status"
                  className={[
                    "mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1",
                    profile.published ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                  ].join(" ")}
                >
                  {profile.published ? (<><Eye className="w-3 h-3" /> Live</>) : (<><EyeOff className="w-3 h-3" /> Hidden</>)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            data-testid="edit-community-profile-close"
            aria-label="Close"
            className="absolute right-3 top-3 sm:right-4 sm:top-4 w-9 h-9 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40"
            disabled={saving}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form body — scrolls independently on smaller viewports */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 md:p-7">
          {loading || !profile ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <LabeledInput
                label="Brand" required value={profile.brand} onChange={(v) => set("brand", v)}
                placeholder="Your brand name" testid="edit-community-brand"
              />
              <LabeledInput
                label="City" value={profile.city} onChange={(v) => set("city", v)}
                placeholder="Los Angeles, CA" icon={MapPin} testid="edit-community-city"
              />
              <SelectField
                label="Category" required value={profile.category} onChange={(v) => set("category", v)}
                options={DIR_CATEGORIES.map((c) => [c, c.charAt(0).toUpperCase() + c.slice(1)])}
                placeholder="Pick a category" testid="edit-community-category"
              />
              <SelectField
                label="Stage" value={profile.stage} onChange={(v) => set("stage", v)}
                options={DIR_STAGES} placeholder="Pick a stage" testid="edit-community-stage"
              />
              <div className="sm:col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Short bio</p>
                <textarea
                  rows={3}
                  value={profile.bio}
                  onChange={(e) => set("bio", e.target.value.slice(0, 400))}
                  placeholder="One paragraph about your brand and what you're building right now."
                  data-testid="edit-community-bio"
                  className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none"
                />
                <p className="text-[10px] text-muted-foreground text-right mt-1">{profile.bio.length}/400</p>
              </div>
              <LabeledInput
                label="Website" value={profile.website} onChange={(v) => set("website", v)}
                placeholder="https://…" icon={Globe} testid="edit-community-website"
              />
              <LabeledInput
                label="Instagram" value={profile.instagram} onChange={(v) => set("instagram", v)}
                placeholder="@yourbrand" icon={Instagram} testid="edit-community-instagram"
              />
              <LabeledInput
                label="LinkedIn" value={profile.linkedin} onChange={(v) => set("linkedin", v)}
                placeholder="in/warren-ezra" icon={Linkedin} testid="edit-community-linkedin"
              />
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="border-t border-shell-border bg-white/95 backdrop-blur p-4 sm:p-5 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            data-testid="edit-community-cancel"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-5 py-2 disabled:opacity-60"
          >
            Cancel
          </button>
          {profile && profile.published && (
            <button
              type="button"
              onClick={() => save(false)}
              disabled={saving || loading}
              data-testid="edit-community-unpublish"
              className="text-xs font-semibold text-muted-foreground hover:text-destructive border border-shell-border rounded-full px-5 py-2 disabled:opacity-60"
            >
              Unpublish
            </button>
          )}
          <button
            type="button"
            onClick={() => save(profile?.published ? true : canPublish)}
            disabled={saving || loading || (!profile?.published && !canPublish)}
            data-testid="edit-community-save"
            className="btn-primary text-sm disabled:opacity-60"
            title={!canPublish && !profile?.published ? "Brand and category are required to publish" : ""}
          >
            {saving
              ? "Saving…"
              : profile?.published
                ? "Save changes"
                : "Save & publish"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LabeledInput({ label, value, onChange, placeholder, icon: Icon, testid, required }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
        {label}{required && <span className="text-primary"> *</span>}
      </p>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />}
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className={[
            "w-full rounded-lg border border-shell-border bg-white py-2 pr-3 text-sm focus:outline-none focus:border-primary",
            Icon ? "pl-9" : "pl-3",
          ].join(" ")}
        />
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, testid, required }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
        {label}{required && <span className="text-primary"> *</span>}
      </p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
      >
        <option value="">{placeholder}</option>
        {options.map(([v, opt]) => <option key={v} value={v}>{opt}</option>)}
      </select>
    </div>
  );
}
